import { NgOptimizedImage } from '@angular/common'
import { Component, inject, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'

import { AccordionModule } from 'primeng/accordion'
import { TreeNode } from 'primeng/api'
import { InputTextModule } from 'primeng/inputtext'
import { PaginatorModule, PaginatorState } from 'primeng/paginator'
import { RippleModule } from 'primeng/ripple'
import { Select } from 'primeng/select'
import { SkeletonModule } from 'primeng/skeleton'

import { CategoryFilterComponent } from 'app/_shared/components/filter-tree/category-filter.component'
import { MobileFiltersComponent } from 'app/_shared/components/mobile-filters/mobile-filters.component'
import { ProductItemComponent } from 'app/_shared/components/product-item/product-item.component'
import { SortDescriptor, SortOption } from 'app/_shared/interfaces/Category'
import { Product } from 'app/_shared/interfaces/Product'
import { LayoutService } from 'app/_shared/services/layout.service'
import { ProductService } from 'app/_shared/services/product.service'
import { AppConfigService } from 'app/app-config.service'
import { ConfigStore } from 'app/store/config.store'

interface Filters {
  global_filter?: string
  sort_field?: string
  sort_order?: string
  'product_categories[]'?: number[]
  'brands[]'?: string[]
  'colors[]'?: string[]
  field?: string
  field_value?: number
}

@Component({
  selector: 'app-product-list',
  standalone: true,
  imports: [
    RippleModule,
    SkeletonModule,
    AccordionModule,
    InputTextModule,
    Select,
    PaginatorModule,
    NgOptimizedImage,
    CategoryFilterComponent,
    ProductItemComponent,
    MobileFiltersComponent,
    FormsModule
  ],
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.scss']
})
export class ProductListComponent implements OnInit {
  private appConfigService: AppConfigService = inject(AppConfigService)
  private productService = inject(ProductService)
  private configStore = inject(ConfigStore)
  private layout = inject(LayoutService)

  serverErrors: { [key: string]: any } = {}
  products: Product[] = []
  skeletons: number[] = Array(6)
    .fill(0)
    .map((_, index) => index + 1)
  isLoading = false
  paginatorOptions = {
    total: 0,
    rows: 9,
    first: 0,
    last_page: 0
  }
  search = ''
  categories: TreeNode[] = []
  selectedCategoryIds: number[] = []
  colors: TreeNode[] = []
  sortingOptions: SortOption[] = [
    { label: 'Newest', value: { id: 0, name: 'created_at', code: 'desc' } },
    { label: 'Price Low to High', value: { id: 1, name: 'final_price', code: 'asc' } },
    { label: 'Price High to Low', value: { id: 2, name: 'final_price', code: 'desc' } },
    { label: 'Name Asc', value: { id: 3, name: 'name', code: 'asc' } },
    { label: 'Name Desc', value: { id: 4, name: 'name', code: 'desc' } }
  ]
  selectedSort?: SortDescriptor
  mobileFiltersVisible = false
  isMobile = this.layout.isMobile
  private storefrontId: number

  ngOnInit(): void {
    this.storefrontId = this.appConfigService.getAppConfig?.id

    const settingsSortRaw = this.configStore.config()?.settings?.sortBy as
      SortDescriptor | string | undefined

    const settingsSort: SortDescriptor | undefined =
      typeof settingsSortRaw === 'string'
        ? (JSON.parse(settingsSortRaw) as SortDescriptor)
        : settingsSortRaw

    if (settingsSort && settingsSort.name !== 'dont_sort') {
      this.selectedSort = this.sortingOptions.find(o => o.value.id === settingsSort.id)?.value
    } else {
      this.selectedSort = undefined
    }

    this.applyFilters()
  }

  applyFilters() {
    // Reset to first page whenever filters change
    this.paginatorOptions.first = 0
    this.fetchStorefrontProducts()
  }

  onPageChange(event: PaginatorState) {
    this.paginatorOptions.rows = event.rows ?? 9
    this.paginatorOptions.first = event.first ?? 0
    this.fetchStorefrontProducts()
  }

  fetchStorefrontProducts() {
    this.isLoading = true

    const pageNumber = Math.floor(this.paginatorOptions.first / this.paginatorOptions.rows) + 1

    const filters: Filters = {
      global_filter: this.search,
      sort_field: this.selectedSort?.name,
      sort_order: this.selectedSort?.code,
      ...(this.selectedCategoryIds &&
        this.selectedCategoryIds.length > 0 && {
          'product_categories[]': this.selectedCategoryIds
        })
    }

    const query = {
      ...filters,
      rows: this.paginatorOptions.rows,
      page: pageNumber
    }

    this.productService.getProducts(this.storefrontId, query).subscribe({
      next: response => {
        this.products = response.data.data
        this.paginatorOptions.total = response.data.total
        this.paginatorOptions.last_page = response.data.last_page
        this.isLoading = false
      },
      error: err => {
        this.serverErrors = err.error.message
        this.isLoading = false
      }
    })
  }
}
